import { randomUUID } from 'node:crypto';
import type { PrismaClient, RefreshToken } from '@prisma/client';
import { SignJWT, jwtVerify } from 'jose';
import type { Config } from '../../core/config.js';
import { randomToken, sha256 } from '../../core/crypto.js';
import { audit } from '../../core/audit.js';

export const ACCESS_TTL_SECONDS = 15 * 60;
export const REFRESH_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** A token rotated this recently and presented again is a benign double-submit (two tabs), not theft. */
export const REUSE_GRACE_MS = 10_000;

const secretKey = (config: Pick<Config, 'JWT_ACCESS_SECRET'>) =>
  new TextEncoder().encode(config.JWT_ACCESS_SECRET);

export async function signAccessToken(
  config: Pick<Config, 'JWT_ACCESS_SECRET'>,
  claims: { userId: number; companyId: number },
): Promise<string> {
  return new SignJWT({ cid: claims.companyId })
    .setProtectedHeader({ alg: 'HS256' })
    .setSubject(String(claims.userId))
    .setIssuedAt()
    .setExpirationTime(`${ACCESS_TTL_SECONDS}s`)
    .sign(secretKey(config));
}

export async function verifyAccessToken(
  config: Pick<Config, 'JWT_ACCESS_SECRET'>,
  token: string,
): Promise<{ userId: number; companyId: number } | null> {
  try {
    const { payload } = await jwtVerify(token, secretKey(config), { algorithms: ['HS256'] });
    const userId = Number(payload.sub);
    const companyId = Number(payload.cid);
    return Number.isInteger(userId) && Number.isInteger(companyId) ? { userId, companyId } : null;
  } catch {
    return null;
  }
}

export interface SessionMeta {
  client: 'web' | 'mobile';
  ip?: string | null;
  deviceLabel?: string | null;
}

/** Creates a refresh token. Only its SHA-256 hash is stored. */
export async function issueRefreshToken(
  db: PrismaClient,
  userId: number,
  meta: SessionMeta,
  familyId: string = randomUUID(),
): Promise<{ token: string; row: RefreshToken }> {
  const token = randomToken(48);
  const row = await db.refreshToken.create({
    data: {
      userId,
      familyId,
      tokenHash: sha256(token),
      client: meta.client,
      ip: meta.ip ?? null,
      deviceLabel: meta.deviceLabel?.slice(0, 200) ?? null,
      expiresAt: new Date(Date.now() + REFRESH_TTL_MS),
    },
  });
  return { token, row };
}

export type RotateResult =
  | { ok: true; token: string; row: RefreshToken; userId: number }
  | { ok: false; reason: 'invalid' | 'expired' | 'reused' };

/** Rotation with reuse detection: presenting an already-rotated token revokes the whole family. */
export async function rotateRefreshToken(
  db: PrismaClient,
  presented: string,
  meta: SessionMeta,
  now: Date = new Date(),
): Promise<RotateResult> {
  const existing = await db.refreshToken.findUnique({ where: { tokenHash: sha256(presented) } });
  if (!existing) return { ok: false, reason: 'invalid' };

  if (existing.revokedAt) {
    const age = now.getTime() - existing.revokedAt.getTime();
    if (existing.replacedById && age <= REUSE_GRACE_MS) return { ok: false, reason: 'invalid' };
    await db.refreshToken.updateMany({
      where: { familyId: existing.familyId, revokedAt: null },
      data: { revokedAt: now },
    });
    const user = await db.user.findUnique({ where: { id: existing.userId } });
    if (user) {
      await audit(
        db,
        { companyId: user.companyId, userId: user.id, ip: meta.ip },
        {
          action: 'auth.refresh_reuse_detected',
          entity: 'refresh_token',
          entityId: existing.id,
          after: { familyId: existing.familyId },
        },
      );
    }
    return { ok: false, reason: 'reused' };
  }
  if (existing.expiresAt <= now) return { ok: false, reason: 'expired' };

  const next = await issueRefreshToken(
    db,
    existing.userId,
    {
      client: existing.client === 'mobile' ? 'mobile' : 'web',
      ip: meta.ip,
      deviceLabel: existing.deviceLabel,
    },
    existing.familyId,
  );
  // Only one concurrent rotation may win the revoke; the loser is treated as a double-submit.
  const won = await db.refreshToken.updateMany({
    where: { id: existing.id, revokedAt: null },
    data: { revokedAt: now, replacedById: next.row.id, lastUsedAt: now },
  });
  if (won.count !== 1) {
    await db.refreshToken.delete({ where: { id: next.row.id } });
    return { ok: false, reason: 'invalid' };
  }
  return { ok: true, token: next.token, row: next.row, userId: existing.userId };
}

export const revokeAllSessions = (db: PrismaClient, userId: number) =>
  db.refreshToken.updateMany({
    where: { userId, revokedAt: null },
    data: { revokedAt: new Date() },
  });
